import { useEffect } from "react";
import { Box } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useBreadcrumb } from "../../contexts/BreadcrumbContext.jsx";
import UsersTable from "../../components/tables/UsersTable.jsx";
import { PageHeader } from "../../theme/components/index.js";
import {
  ContentDialog,
  ContentDialogInit,
} from "components/MDDForm/components/ContentDialog.jsx";
import { Form, FormRenderer } from "../../components/MDDForm/MDDForm.jsx";
import { fdefUploadBenutzer } from "../../formdefs/upload_benutzer.js";
import { getRWObjectCopy } from "../../components/MDDForm/helper.js";
import { backendGet } from "../../services/dataService.js";
import { useUserActions } from "../../hooks/useUserActions.jsx";
import AdminUserForm from "./components/AdminUserForm.jsx";
import UserImportHelp from "../../components/UserImportHelp.jsx";
import AssignmentImportTabPage from "./AssignmentImportTabPage.jsx";
import { useSnackbar } from "notistack";
import { useUser } from "../../hooks/user.js";

/**
 * Users List Page
 * Shows a list of users with create/edit/import functionality
 */
export default function UserIndexPage() {
  const { setBreadcrumbs } = useBreadcrumb();
  const { openCD, setOpenCD, tca, setTca, CDActions, setCDActions } =
    ContentDialogInit();
  const { createOrUpdateUser, uploadUserList } = useUserActions();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  const is_sys_admin = user?.role_names?.includes("sysadmin");
  const can_manage_users = !!is_sys_admin;

  useEffect(() => {
    setBreadcrumbs([
      { name: "Home", path: "/home" },
      { name: "Benutzer", path: "/admin/users" },
    ]);
  }, [setBreadcrumbs]);

  const closeDialog = () => {
    setOpenCD(false);
  };

  const openEditUserModal = (userData) => {
    if (!can_manage_users) {
      enqueueSnackbar("Keine Berechtigung", { variant: "error" });
      return;
    }
    showEditCreateModal(userData, "Benutzer bearbeiten");
  };

  const openCreateUserModal = () => {
    if (!can_manage_users) {
      enqueueSnackbar("Keine Berechtigung", { variant: "error" });
      return;
    }
    showEditCreateModal({}, "Benutzer erstellen");
  };

  const showEditCreateModal = (model, title) => {
    setOpenCD(true);
    setTca({
      title: title,
      content: (
        <AdminUserForm
          initialData={model}
          isEditMode={!!model.id}
          onSubmit={async (formData, formMethods) => {
            try {
              await createOrUpdateUser(formData);
              closeDialog();
              enqueueSnackbar("Änderungen gespeichert.", {
                variant: "success",
              });
              return true;
            } catch (error) {
              if (formMethods.setError) {
                // Handle form errors if backend returns them
                console.error("Error updating user:", error);
              }
              return false;
            }
          }}
          onCancel={undefined}
          hideActions={true}
        />
      ),
      actions: null,
    });
  };

  const openImportUsersModal = () => {
    if (!can_manage_users) {
      enqueueSnackbar("Keine Berechtigung", { variant: "error" });
      return;
    }

    let fdef = getRWObjectCopy(fdefUploadBenutzer);
    setCDActions(null);
    setOpenCD(true);
    setTca({
      title: "Upload Benutzer für Kunde",
      content: (
        <>
          <Form>
            <FormRenderer
              fdef={fdef}
              model={{}}
              variant="filled"
              editMode={true}
              showActions={false}
              showCancel={true}
              showToggleEdit={false}
              showErrorText={true}
              actionsPosition="bottom"
              apiGet={backendGet}
              cancelAction={closeDialog}
              submitAction={{
                method: "callback",
                callback: async (formData) => {
                  await uploadUserList(formData);
                  closeDialog();
                  enqueueSnackbar("Benutzer wurden importiert.", {
                    variant: "success",
                  });
                  // Note: For bulk imports, the table will need to refetch
                  // This is handled by triggering a data reload
                  window.location.reload();
                },
              }}
              setActions={setCDActions}
            />
          </Form>
          <UserImportHelp />
        </>
      ),
      actions: null,
    });
  };

  const showAssignments = location.pathname === "/admin/users/assignments";

  return (
    <>
      {showAssignments ? (
        <Box>
          <PageHeader
            title="Vertragszuordnung"
            subtitle="Benutzer den Verträgen und Objekten zuordnen"
          />
          <AssignmentImportTabPage />
        </Box>
      ) : (
        // Header-mode table (matches the click dummy: single list, no tab bar).
        // The Vertragszuordnung admin tool is reached via a toolbar action.
        <UsersTable
          pageTitle="Benutzer"
          pageSubtitle="Alle Benutzer und Rollen der Kanzlei"
          onRowEdit={openEditUserModal}
          onClickNew={openCreateUserModal}
          onClickImport={openImportUsersModal}
          onClickAssignments={
            can_manage_users
              ? () => navigate("/admin/users/assignments")
              : undefined
          }
        />
      )}
      <ContentDialog
        openCD={openCD}
        setOpenCD={setOpenCD}
        CDActions={CDActions}
        tca={tca}
      />
    </>
  );
}
